import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import ImageWidgetUploadInput from "../../../../parts/inputs/image-widget-upload";
import { SearchDropdown } from "../../../../parts/inputs/search-dropdown";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";

const DEFAULT_SIMPLIFIED_MEALS = [
  { type: "Breakfast", order: 1, foods: [] },
  { type: "Lunch", order: 2, foods: [] },
  { type: "Snack", order: 3, foods: [] },
  { type: "Dinner", order: 4, foods: [] },
];

export const InAppMealPlannerModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    mealPlanTemplates = [],
    origin,
    activeWidget,
    query,
    mealPlanLibraries = [],
    activeUserToRequest,
  } = locals;
  const { selectedMealPlanLibraryId } = query;
  const selectedLibrary =
    mealPlanLibraries.find(({ _id }) => _id === selectedMealPlanLibraryId) ||
    null;

  const { image: imageProps = "", mealPlan = {} } = selectedLibrary || {};
  const {
    weeks = [],
    mealPlanId = null,
    planMode: libraryPlanMode,
  } = mealPlan || {};

  /** * Function to parse the weeks (because we are receiving the weeks with join, so, we have to split to transform it in array)
   *
   * @param {*} weeks
   * @returns
   */
  const parseWeeks = (weeks) => {
    if (weeks == null) return [];
    if (Array.isArray(weeks)) return weeks.map((w) => Number(w));
    if (typeof weeks === "number") return [weeks];
    if (typeof weeks === "string") {
      return weeks
        .split(",")
        .map((w) => w.trim())
        .filter(Boolean)
        .map((w) => Number(w));
    }
    return [];
  };

  const allPlanOptions = mealPlanTemplates.map(
    ({ _id, title, weeks = [], description, planMode }) => ({
      value: _id,
      label: title,
      weeks,
      description,
      planMode: planMode || "detailed",
    })
  );

  const originalWeeks = parseWeeks(weeks);
  const originalPlan =
    allPlanOptions.find((opt) => opt.value === mealPlanId) || null;
  const originalImage = imageProps?.s3Path || null;

  const initialMealPlanType =
    libraryPlanMode === "simplified" ? "simplified" : "detailed";
  const [mealPlanType, setMealPlanType] = useState(initialMealPlanType);
  const [imageState, setImageState] = useState(
    imageProps ? imageProps?.s3Path : null
  );

  const [state, setState] = useState({
    image: imageProps,
    selectedWeeks: originalWeeks,
    selectedPlan: originalPlan,
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [simplifiedForm, setSimplifiedForm] = useState({
    name: selectedLibrary?.name ?? "",
    description: selectedLibrary?.description ?? "",
  });

  const { image, selectedWeeks, selectedPlan } = state;

  // Dropdown always shows only detailed templates (when visible). Simplified path uses a button later.
  const searchOptions = allPlanOptions.filter(
    (opt) => opt.planMode !== "simplified"
  );

  // Detect changes in the state
  const hasChanges =
    JSON.stringify([...selectedWeeks].sort((a, b) => a - b)) !==
      JSON.stringify([...originalWeeks].sort((a, b) => a - b)) ||
    (selectedPlan?.value || null) !== (originalPlan?.value || null) ||
    (imageState || null) !== (originalImage || null) ||
    (mealPlanType === "simplified" &&
      (simplifiedForm.name !== (selectedLibrary?.name ?? "") ||
        simplifiedForm.description !== (selectedLibrary?.description ?? "")));

  const handleImageUpload = (newImage) => {
    setImageState(newImage);
  };

  const imageWidgetProps = {
    name: "image",
    prefix: "",
    defaultValue: image,
    showIcon: false,
    ...(selectedMealPlanLibraryId
      ? {
          backgroundedit: image?.s3Path || "",
          hasImage: true,
          handleImageUpload,
        }
      : {
          handleImageUpload,
        }),
  };

  const primaryLabel = selectedMealPlanLibraryId
    ? t("widgets.inAppMealPlanner.editMealPlan")
    : t("widgets.inAppMealPlanner.addMealPlan");

  // Simplified: valid when we have image + name (description can be empty per API).
  // Detailed: valid when weeks + template selected + image.
  const baseFormValid =
    mealPlanType === "detailed"
      ? selectedWeeks.length > 0 && selectedPlan && imageState
      : Boolean(imageState && simplifiedForm.name?.trim());

  const isFormValid = selectedMealPlanLibraryId
    ? hasChanges && baseFormValid
    : baseFormValid;

  const handleMealPlanTypeChange = (type) => {
    setMealPlanType(type);
    updateState({ selectedPlan: null, selectedWeeks: [] });
  };

  const updateState = (newValues) => {
    setState((prevState) => ({ ...prevState, ...newValues }));
  };

  const handleSelectionChange = (index) => {
    updateState({
      selectedWeeks: selectedWeeks.includes(index)
        ? selectedWeeks.filter((week) => week !== index)
        : [...selectedWeeks, index],
    });
  };

  const isSimplifiedCreate =
    mealPlanType === "simplified" && !selectedMealPlanLibraryId;

  let formAction;
  if (isSimplifiedCreate) {
    formAction = "/actions/meal-planner/create-simplified-meal-plan-for-widget";
  } else if (selectedMealPlanLibraryId) {
    formAction = `/actions/meal-plan-library/save/${selectedMealPlanLibraryId}`;
  } else {
    formAction = "/actions/meal-plan-library/save/";
  }

  const simplifiedDefaultOption = {
    title: "Option 1",
    meals: DEFAULT_SIMPLIFIED_MEALS,
  };
  const simplifiedHiddenFields = isSimplifiedCreate
    ? [
        { name: "widgetId", value: activeWidget?._id ?? "" },
        { name: "type", value: "library" },
        { name: "planMode", value: "simplified" },
        { name: "coachId", value: activeUserToRequest?._id ?? "" },
        { name: "createdBy", value: activeUserToRequest?._id ?? "" },
        { name: "athleteId", value: "" },
        { name: "published", value: "false" },
        {
          name: "trainingDayOptions",
          value: JSON.stringify([simplifiedDefaultOption]),
        },
        {
          name: "restDayOptions",
          value: JSON.stringify([simplifiedDefaultOption]),
        },
      ]
    : [];

  let mealPlanValue = null;
  if (!isSimplifiedCreate) {
    const mealPlanPayload =
      mealPlanType === "simplified"
        ? {
            mealPlanId: selectedPlan?.value || mealPlanId || "",
            planMode: "simplified",
          }
        : {
            mealPlanId: selectedPlan?.value || "",
            planMode: "detailed",
            weeks: [...(selectedWeeks || [])].sort((a, b) => a - b),
          };
    mealPlanValue = JSON.stringify(mealPlanPayload);
  }

  return (
    <Modal
      className="in-app-meal-planner-modal"
      title={t("widgets.inAppMealPlanner.title")}
      toClear={["selectedMealPlanLibraryId"]}
    >
      {mealPlanTemplates.length > 0 ? (
        <form
          className={`body ${isDropdownOpen ? "dropdown-open" : ""}`}
          encType="multipart/form-data"
          method="POST"
          data-noreset
          action={formAction}
        >
          <input
            name="widget"
            className="hidden"
            defaultValue={activeWidget?._id}
          />
          {mealPlanType === "detailed" && (
            <>
              <input
                readOnly
                name="name"
                className="hidden"
                value={selectedPlan?.label || ""}
              />
              <input
                readOnly
                name="description"
                className="hidden"
                value={selectedPlan?.description || ""}
              />
            </>
          )}
          {mealPlanValue !== null && (
            <input
              readOnly
              name="mealPlan"
              className="hidden"
              value={mealPlanValue}
            />
          )}
          {simplifiedHiddenFields.map(({ name, value }) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
          <p className="s2">
            {t("widgets.inAppMealPlanner.selectMealPlanAndWeek")}
          </p>

          <ImageWidgetUploadInput {...imageWidgetProps} />

          <div className="meal-plan-type-section">
            <label htmlFor="meal-plan-type">
              {t("widgets.inAppMealPlanner.selectMealPlanType")}
            </label>
            <div className="meal-plan-type-radios" id="meal-plan-type">
              <label className="radio-option">
                <input
                  type="radio"
                  name="mealPlanType"
                  value="detailed"
                  checked={mealPlanType === "detailed"}
                  onChange={() => handleMealPlanTypeChange("detailed")}
                />
                <span>{t("widgets.inAppMealPlanner.detailedMealPlan")}</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="mealPlanType"
                  value="simplified"
                  checked={mealPlanType === "simplified"}
                  onChange={() => handleMealPlanTypeChange("simplified")}
                />
                <span>{t("widgets.inAppMealPlanner.simplifiedMealPlan")}</span>
              </label>
            </div>
          </div>

          {mealPlanType === "detailed" && (
            <div>
              <label htmlFor="plans">
                {t("widgets.inAppMealPlanner.selectMealPlan")}
              </label>

              <SearchDropdown
                id="plans"
                name="plans"
                options={searchOptions}
                handleChange={(value) =>
                  updateState({ selectedPlan: value, selectedWeeks: [] })
                }
                value={selectedPlan?.label}
                valueKey="label"
                placeholder={t(
                  "widgets.inAppMealPlanner.selectMealPlanPlaceholder"
                )}
                onOpenChange={setIsDropdownOpen}
              />
            </div>
          )}

          {mealPlanType === "detailed" && selectedPlan && (
            <>
              <div className="weeks-container">
                <label htmlFor="week-list">
                  {t("widgets.inAppMealPlanner.selectWeek")}
                </label>
                <div className="week-list" id="week-list">
                  <ul>
                    {selectedPlan?.weeks.map((_, index) => (
                      <li key={`${selectedPlan?._id}-${index}`}>
                        <SvgIcon
                          onClick={() => handleSelectionChange(index)}
                          src={`/img/new-icons/${
                            selectedWeeks.includes(index)
                              ? "checked-square.svg"
                              : "unchecked-square.svg"
                          }`}
                          noFilterOnLight={selectedWeeks.includes(index)}
                        />
                        <span>
                          {t("widgets.inAppMealPlanner.week")} {index + 1}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="chip selected-info">
                {t("widgets.inAppMealPlanner.selectedWeeksInfo")}
              </p>
            </>
          )}

          {mealPlanType === "simplified" && (
            <>
              <div className="input-group">
                <label htmlFor="simplified-name">
                  {t("widgets.inAppMealPlanner.nameLabel")}
                </label>
                <input
                  id="simplified-name"
                  name="name"
                  type="text"
                  value={simplifiedForm.name}
                  onChange={(e) =>
                    setSimplifiedForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="input-group">
                <label htmlFor="simplified-description">
                  {t("widgets.inAppMealPlanner.descriptionLabel")}
                </label>
                <input
                  id="simplified-description"
                  name="description"
                  type="text"
                  value={simplifiedForm.description}
                  onChange={(e) =>
                    setSimplifiedForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="meal-plan-library-simplified-actions">
                {mealPlanId && libraryPlanMode === "simplified" ? (
                  <a
                    className="button add"
                    data-button-type="primary"
                    href={`${origin}/coaching-portal/templates/edit-meal-plan?mealPlan=${mealPlanId}&widget=${activeWidget?._id ?? ""}`}
                  >
                    {t("widgets.inAppMealPlanner.openTemplateEditor")}
                  </a>
                ) : (
                  <button
                    type="submit"
                    className="button add"
                    data-button-type="primary"
                    disabled={!isFormValid}
                  >
                    {t("widgets.inAppMealPlanner.createSimplifiedMealPlan")}
                  </button>
                )}
              </div>
            </>
          )}

          <ActionButtons
            className="buttons"
            primaryLabel={primaryLabel}
            primaryDisabled={!isFormValid}
          />
        </form>
      ) : (
        <div className="body text-center pb-8 pt-14!">
          <p className="s2 font-bold mb-2">
            {t("widgets.inAppMealPlanner.noTemplatesAdded")}
          </p>
          <p className="p4 opacity-70">
            {t("widgets.inAppMealPlanner.noTemplatesHint")}
          </p>
          <ActionButtons
            className="template-buttons"
            primaryLabel={t("widgets.inAppMealPlanner.createMealPlanTemplate")}
            onPrimaryClick={() => {
              window.location.href = `${origin}/coaching-portal/all-templates?owner=coach&tabView=meal-plans-tab&modal[type]=handle-meal-plan`;
            }}
          />
        </div>
      )}
    </Modal>
  );
};

function ActionButtons({
  primaryLabel,
  onPrimaryClick,
  primaryDisabled = false,
  className = "",
}) {
  const { t } = useTranslation("coaching-portal");
  /**
   * Close modal
   */
  const handleCancel = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: { "modal[type]": "", selectedMealPlanLibraryId: "" },
    });
  };

  return (
    <div className={`${className}`}>
      <button className="cancel" onClick={handleCancel} type="button">
        {t("widgets.inAppMealPlanner.cancel")}
      </button>
      <button
        type="submit"
        className="add"
        data-button-type="primary"
        onClick={onPrimaryClick}
        disabled={primaryDisabled}
      >
        {primaryLabel}
      </button>
    </div>
  );
}

InAppMealPlannerModal.propTypes = {
  locals: PropTypes.object,
};

ActionButtons.propTypes = {
  primaryLabel: PropTypes.string,
  onPrimaryClick: PropTypes.func,
  primaryDisabled: PropTypes.bool,
  className: PropTypes.string,
};
